// Export a play as PDF or PNG (client-side)
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const NAVY = '#0A192F';
const GOLD = '#D4AF37';

async function captureSteps(stepRefs) {
  const images = [];
  for (const r of stepRefs) {
    if (!r) continue;
    const canvas = await html2canvas(r, {
      backgroundColor: NAVY,
      scale: 1.5,
      useCORS: true,
      logging: false,
    });
    images.push(canvas.toDataURL('image/jpeg', 0.85));
  }
  return images;
}

export async function exportPlayAsPDF(play, stepRefs, { coachName = '' } = {}) {
  const images = await captureSteps(stepRefs);
  // A4 landscape
  const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();

  // Cover page
  pdf.setFillColor(10, 25, 47);
  pdf.rect(0, 0, pageW, pageH, 'F');
  pdf.setTextColor(212, 175, 55);
  pdf.setFontSize(10);
  pdf.text('PLAYMASTER IA', 15, 18);
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(32);
  pdf.text(play.name || 'Jugada', 15, 60);
  pdf.setTextColor(212, 175, 55);
  pdf.setFontSize(11);
  pdf.text(`${(play.category || '').toUpperCase()} · ${play.subcategory || ''} · ${(play.difficulty || '').toUpperCase()}`, 15, 70);
  pdf.setTextColor(220, 220, 220);
  pdf.setFontSize(11);
  const desc = (play.description || '').slice(0, 320);
  const wrapped = pdf.splitTextToSize(desc, pageW - 30);
  pdf.text(wrapped, 15, 85);
  pdf.setTextColor(150, 150, 150);
  pdf.setFontSize(9);
  pdf.text(`Coach: ${coachName} · Tasa de éxito: ${Math.round((play.success_rate || 0.6) * 100)}%`, 15, pageH - 12);

  // One page per step
  images.forEach((img, i) => {
    pdf.addPage();
    pdf.setFillColor(10, 25, 47);
    pdf.rect(0, 0, pageW, pageH, 'F');
    pdf.setTextColor(212, 175, 55);
    pdf.setFontSize(10);
    pdf.text(`PASO ${i + 1} / ${images.length}`, 15, 15);
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(16);
    pdf.text(play.name || 'Jugada', 15, 25);
    // image — keep aspect ratio
    const margin = 15;
    const availW = pageW - margin * 2;
    const availH = pageH - 40;
    const img1 = new Image();
    img1.src = img;
    // Use a square fit for SVG screenshot
    const size = Math.min(availW, availH);
    const x = (pageW - size) / 2;
    pdf.addImage(img, 'JPEG', x, 30, size, size);

    const step = play.steps?.[i];
    if (step?.description) {
      pdf.setTextColor(220, 220, 220);
      pdf.setFontSize(10);
      const w = pdf.splitTextToSize(`Paso ${i + 1}: ${step.description}`, pageW - 30);
      pdf.text(w, 15, pageH - 18);
    }
  });

  const filename = `${(play.name || 'jugada').replace(/[^\w]+/g, '_')}.pdf`;
  pdf.save(filename);
  return filename;
}

export async function exportPlayAsPNG(play, stepRefs) {
  const images = await captureSteps(stepRefs);
  if (!images.length) return null;
  // Compose a single tall image with all steps
  const canvas = document.createElement('canvas');
  const W = 1100;
  const H = images.length * 1100 + 80;
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = NAVY;
  ctx.fillRect(0, 0, W, H);
  ctx.fillStyle = GOLD;
  ctx.font = 'bold 32px Oswald, sans-serif';
  ctx.fillText((play.name || 'Jugada').toUpperCase(), 30, 50);

  // Sequentially draw each step image
  for (let i = 0; i < images.length; i++) {
    await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const y = 80 + i * 1100;
        ctx.drawImage(img, 30, y, W - 60, 1000);
        ctx.fillStyle = GOLD;
        ctx.font = 'bold 18px Oswald, sans-serif';
        ctx.fillText(`PASO ${i + 1}`, 30, y + 1030);
        ctx.fillStyle = '#dddddd';
        ctx.font = '16px IBM Plex Sans, sans-serif';
        ctx.fillText((play.steps?.[i]?.description || '').slice(0, 110), 30, y + 1060);
        resolve();
      };
      img.src = images[i];
    });
  }

  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(play.name || 'jugada').replace(/[^\w]+/g, '_')}.png`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      resolve(blob);
    }, 'image/png', 0.95);
  });
}

export async function shareViaWhatsApp(play, blob, filename = 'jugada.png') {
  // Build a public share URL so messengers (WhatsApp/Telegram/etc.) render a
  // rich OpenGraph card with PlayMaster IA logo + play name + success rate.
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const shareUrl = play?.id && base ? `${base}/api/share/play/${play.id}` : '';
  const pct = play?.success_rate != null ? `${Math.round(play.success_rate * 100)}% éxito` : '';
  const headline = `📋 ${play?.name || 'Jugada'} — PlayMaster IA`;
  const lines = [headline];
  if (pct) lines.push(pct);
  if (shareUrl) lines.push(shareUrl);
  const text = lines.join('\n');

  if (navigator.share && blob) {
    try {
      const file = new File([blob], filename, { type: blob.type });
      await navigator.share({
        files: [file],
        title: play?.name || 'PlayMaster IA',
        text,
        url: shareUrl || undefined,
      });
      return true;
    } catch { /* fall through */ }
  }
  // fallback: WhatsApp Web with text including share URL (so the messenger shows the OG card)
  const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
  window.open(url, '_blank');
  return false;
}
